import { OFFICIAL_FEES, FEE_DISCLAIMER_TH, type OfficialFeeKey } from "@/data/official-fees";

type FeeLocale = "th" | "en" | "zh" | "ja" | "ko" | "vi" | "id";

const UI: Record<FeeLocale, { heading: string; time: string; authority: string; source: string; verified: string; note: string }> = {
  th: {
    heading: "ค่าธรรมเนียมราชการที่เกี่ยวข้อง (อ้างอิงแหล่งทางการ)",
    time: "ระยะเวลา", authority: "หน่วยงาน", source: "แหล่งอ้างอิง", verified: "ตรวจสอบเมื่อ",
    note: FEE_DISCLAIMER_TH,
  },
  en: {
    heading: "Related government fees (cited from official sources)",
    time: "Processing time", authority: "Authority", source: "Source", verified: "Verified on",
    note: "Government fees and processing times are set by the authorities and may change without notice. Figures above are quoted from the official source shown and exclude our professional fee.",
  },
  zh: {
    heading: "相关政府费用（引自官方来源）",
    time: "办理时间", authority: "主管机关", source: "来源", verified: "核实日期",
    note: "政府费用与办理时间由主管机关制定，可能随时调整。以上数字引自所示官方来源，不含我们的服务费。",
  },
  ja: {
    heading: "関連する政府手数料（公式情報に基づく）",
    time: "所要期間", authority: "所管官庁", source: "出典", verified: "確認日",
    note: "政府手数料および所要期間は官庁が定めるもので、予告なく変更される場合があります。上記は表示の公式出典からの引用で、当事務所の報酬は含みません。",
  },
  ko: {
    heading: "관련 정부 수수료 (공식 출처 인용)",
    time: "처리 기간", authority: "담당 기관", source: "출처", verified: "확인일",
    note: "정부 수수료와 처리 기간은 담당 기관이 정하며 사전 통보 없이 변경될 수 있습니다. 위 금액은 표시된 공식 출처 인용이며 당사 수임료는 포함되지 않습니다.",
  },
  vi: {
    heading: "Phí nhà nước liên quan (dẫn từ nguồn chính thức)",
    time: "Thời gian xử lý", authority: "Cơ quan", source: "Nguồn", verified: "Kiểm tra ngày",
    note: "Phí nhà nước và thời gian xử lý do cơ quan quy định và có thể thay đổi. Các số liệu trên được dẫn từ nguồn chính thức nêu trên, không bao gồm phí dịch vụ của chúng tôi.",
  },
  id: {
    heading: "Biaya resmi terkait (dikutip dari sumber resmi)",
    time: "Waktu proses", authority: "Instansi", source: "Sumber", verified: "Diverifikasi",
    note: "Biaya resmi dan waktu proses ditetapkan instansi dan dapat berubah. Angka di atas dikutip dari sumber resmi yang tertera dan tidak termasuk jasa profesional kami.",
  },
};

/**
* Renders a citation-backed government-fee block.
* Use this anywhere a page states an official fee, so the number always
* travels with its authority, verification date and primary source.
*/
export function OfficialFeeNote({ keys, locale = "th" }: { keys: OfficialFeeKey[]; locale?: FeeLocale }) {
  const fees = keys.map((k) => OFFICIAL_FEES[k]);
  const t = UI[locale] ?? UI.th;
  return (
    <aside className="my-6 rounded-lg border border-border bg-muted/40 p-4 text-sm">
      <h2 className="mt-0 text-base font-semibold">{t.heading}</h2>

      <dl className="m-0 space-y-3">
        {fees.map((f) => (
          <div key={f.key}>
            <dt className="font-medium">{f.labelTh}</dt>
            <dd className="m-0 text-muted-foreground">
              <span>{f.amountTh}</span>
              <br />
              <span>{t.time}: {f.timeTh}</span>
              <br />
              <span>{t.authority}: {f.authorityTh}</span>
              <br />
              <a href={f.sourceUrl} rel="nofollow noopener" target="_blank">
                {t.source}: {f.sourceLabel}
              </a>
              <span> · {t.verified} {locale === "th" ? f.verifiedOnTh : f.verifiedOnISO}</span>
            </dd>
          </div>
        ))}
      </dl>
      <p className="mb-0 mt-3 text-xs text-muted-foreground">{t.note}</p>

    </aside>
  );
}
